import React, {
  cloneElement,
  Children,
  useCallback,
  useEffect,
  useRef,
} from 'react';

export type TooltipGravity =
  | 'n' | 's' | 'e' | 'w'
  | 'nw' | 'ne' | 'sw' | 'se'
  | 'auto';

export interface TooltipOptions {
  gravity?: TooltipGravity;
  trigger?: 'hover' | 'manual';
  offset?: number;
  fade?: boolean;
  title?: string;
}

export interface TooltipHandle {
  show: (message?: string) => void;
  hide: () => void;
}

function getJQuery(): any {
  return (window as any).jQuery || (window as any).$;
}

function resolveGravity($: any, gravity: TooltipGravity | undefined) {
  if (!gravity || gravity === 'auto') {
    return $?.fn?.tipsy?.autoNS;
  }
  return gravity;
}

// Marker used to distinguish aria-labels we set from caller-provided ones, so
// we can update or clear them without clobbering an explicit label the caller
// chose.
const ARIA_LABEL_OWNED_ATTR = 'data-tooltip-aria-owned';

// Mirror the tooltip text onto aria-label so screen readers announce it
// without requiring hover/focus to fire tipsy. Only set the label if the
// caller hasn't already provided one — their explicit label wins.
function applyAriaLabel(el: HTMLElement, text: string): void {
  const existing = el.getAttribute('aria-label');
  if (existing != null && el.getAttribute(ARIA_LABEL_OWNED_ATTR) == null) {
    return;
  }
  el.setAttribute('aria-label', text);
  el.setAttribute(ARIA_LABEL_OWNED_ATTR, '');
}

function clearOwnedAriaLabel(el: HTMLElement): void {
  if (el.getAttribute(ARIA_LABEL_OWNED_ATTR) != null) {
    el.removeAttribute('aria-label');
    el.removeAttribute(ARIA_LABEL_OWNED_ATTR);
  }
}

// Best-effort: tag the live tipsy popup with role="tooltip" and link it back
// to the trigger via aria-describedby. Tipsy stores the popup jQuery object
// at `$.data(el, 'tipsy').$tip` — if the internal shape ever changes we just
// silently skip the enhancement (the aria-label fallback still covers a11y).
let tooltipIdCounter = 0;
function linkPopupToTrigger($: any, el: HTMLElement): void {
  try {
    const data = $.data(el, 'tipsy');
    const $tip = data?.$tip;
    const tip: HTMLElement | undefined = $tip?.[0];
    if (!tip) return;
    if (!tip.id) {
      tooltipIdCounter += 1;
      tip.id = `kollab-tooltip-${tooltipIdCounter}`;
    }
    tip.setAttribute('role', 'tooltip');
    el.setAttribute('aria-describedby', tip.id);
  } catch {}
}

function unlinkPopupFromTrigger(el: HTMLElement): void {
  el.removeAttribute('aria-describedby');
}

export function useTooltip(
  ref: React.RefObject<HTMLElement | null>,
  options?: TooltipOptions,
): TooltipHandle {
  const optionsRef = useRef(options);
  optionsRef.current = options;

  // The element Tipsy is currently attached to. We can't rely on `ref.current`
  // read at render time as an effect dependency: when the trigger is rendered
  // conditionally (e.g. a button inside a popover), it isn't committed yet on
  // the render that mounts it, so `ref.current` is still null. Populating a ref
  // doesn't trigger a re-render, so a `[ref.current]`-keyed effect would never
  // re-run with the real element and Tipsy would never attach. Instead we run
  // after every commit and (re)attach only when the actual element changes.
  const attachedElRef = useRef<HTMLElement | null>(null);

  const detach = useCallback((el: HTMLElement, $: any) => {
    try {
      $(el).tipsy('hide');
    } catch {}
    unlinkPopupFromTrigger(el);
    clearOwnedAriaLabel(el);
  }, []);

  // Attach/detach whenever the trigger element actually mounts or changes.
  // Cheap on unrelated re-renders: one identity comparison, then return.
  useEffect(() => {
    const $ = getJQuery();
    const el = ref.current;
    if (el === attachedElRef.current) return;

    const prev = attachedElRef.current;
    if (prev && $) detach(prev, $);

    attachedElRef.current = el;
    if (!el || !$) return;

    const opts = optionsRef.current ?? {};
    if (opts.title !== undefined) {
      el.setAttribute('original-title', opts.title);
      el.removeAttribute('title');
      applyAriaLabel(el, opts.title);
    }

    try {
      $(el).tipsy({
        trigger: opts.trigger ?? 'hover',
        gravity: resolveGravity($, opts.gravity),
        offset: opts.offset,
        fade: opts.fade,
      });
    } catch {
      // jQuery / tipsy not loaded (e.g. test env) — nothing to attach.
    }
  });

  // Final cleanup on unmount so Tipsy popups (appended to <body>) don't linger
  // after React removes the trigger.
  useEffect(() => {
    return () => {
      const $ = getJQuery();
      const el = attachedElRef.current;
      if (el && $) detach(el, $);
      attachedElRef.current = null;
    };
  }, [detach]);

  // Update if title changes
  useEffect(() => {
    const $ = getJQuery();
    const el = ref.current;
    if (!el || !$) return;

    const title = optionsRef.current?.title;
    if (title === undefined) return;

    el.setAttribute('original-title', title);
    el.removeAttribute('title');
    applyAriaLabel(el, title);

    try {
      const tipsy = $.data(el, 'tipsy');
      const tip = tipsy?.$tip?.[0] as HTMLElement | undefined;
      const inner = tip?.querySelector?.('.tipsy-inner');
      if (inner) {
        inner.textContent = title;
      }
    } catch {}
  }, [ref, options?.title]);

  const show = useCallback((message?: string) => {
    const el = ref.current;
    const $ = getJQuery();
    if (!el || !$) return;
    if (message !== undefined) {
      el.setAttribute('original-title', message);
      el.removeAttribute('title');
      applyAriaLabel(el, message);
    }
    try {
      $(el).tipsy('show');
      linkPopupToTrigger($, el);
    } catch {}
  }, [ref]);

  const hide = useCallback(() => {
    const el = ref.current;
    const $ = getJQuery();
    if (!el || !$) return;
    try {
      $(el).tipsy('hide');
    } catch {}
    unlinkPopupFromTrigger(el);
  }, [ref]);

  return { show, hide };
}

export interface TooltipProps extends TooltipOptions {
  title: string;
  children: React.ReactNode;
}

export function Tooltip({ children, ...options }: TooltipProps) {
  const ref = useRef<HTMLElement | null>(null);
  useTooltip(ref, { trigger: 'hover', ...options });

  const child = Children.only(children) as React.ReactElement & {
    ref?: React.Ref<HTMLElement>;
  };

  const mergedRef = (node: HTMLElement | null) => {
    ref.current = node;
    const childRef = child.ref;
    if (typeof childRef === 'function') {
      childRef(node);
    } else if (childRef && typeof childRef === 'object') {
      (childRef as React.MutableRefObject<HTMLElement | null>).current = node;
    }
  };

  return cloneElement(child, { ref: mergedRef } as Partial<unknown> as object);
}

// Hide every tipsy popup owned by `.tooltip` triggers inside `wrapper`. Tipsy
// appends popups to <body>, so when React unmounts a wrapper its descendant
// tooltips can linger as orphaned overlays — scoping cleanup to the wrapper
// avoids killing tooltips owned by unrelated UI.
export function hideTooltipsIn(wrapper: HTMLElement | null): void {
  if (!wrapper) return;
  const $ = getJQuery();
  if (!$) return;
  try {
    wrapper.querySelectorAll('.tooltip').forEach((trigger) => {
      try { $(trigger).tipsy('hide'); } catch {}
      if (trigger instanceof HTMLElement) {
        unlinkPopupFromTrigger(trigger);
      }
    });
  } catch {}
}
